/**
 * Loop trace room tool (experimental) — the wall tracer's UX on the
 * skeletonized composition map.
 *
 * The capture pipeline (maskComposition) reduces the viewport density to
 * ~1px wall centrelines whose surviving loops are real enclosed regions.
 * Hovering traces the SHORTEST LOOP through the wall pixel under the
 * cursor (loopTracer: flood out from the pixel, every traveled pixel
 * marked once; where two branches meet, the joined parent chains are the
 * smallest ring) and previews the cleaned polygon live. A click commits
 * exactly what the hover shows and hands the polygon to the hosted
 * LineTool room-edit session — the same magic-room flow the wall tracer
 * and lidar probe use. One-click prototype: correction waypoints come
 * later.
 *
 * Debug panel: the shared MaskToolDebugKit with THIS tool's own settings
 * file (loop-tracer-settings.json) — stage images, live sliders, export.
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Viewer } from '../../../viewer/viewer';
import { IPersona } from '../../types';
import { FloorplanToolBase } from './FloorplanToolBase';
import { CoordinateTransformer } from '../modules/utils/CoordinateTransformer';
import { ImageCapture } from '../modules/capture/ImageCapture';
import { PolygonProcessor } from '../../../utils/PolygonProcessor';
import { createPolygon, Point2D } from '../../../utils/segmentation/types';
import { PointoramaRoom } from '../../../utils/PointoramaRoom';
import { PolygonRenderer3D } from '../modules/segmentation/PolygonRenderer3D';
import { TracerMask, TracerPoint } from '../modules/wallTracer/snakeTracer';
import { removeSmallBlobs } from '../modules/lidarProbe/lidarProbe';
import { MaskPrepOptions, MaskStageSink, DEFAULT_MASK_PREP_OPTIONS, composeWorkingMask, dilateMask } from '../modules/lidarProbe/maskComposition';
import { LoopTraceOptions, DEFAULT_LOOP_TRACE_OPTIONS, traceShortestLoop } from '../modules/lidarProbe/loopTracer';
import { DepthSegmentationPipeline } from '../../../utils/segmentation/DepthSegmentationCore';
import { SubToolHosting } from '../../SubToolHosting';
import { SubToolInputKind } from '../../SubTool';
import { LineTool } from './LineTool';
import { RoomKindAdapter } from './line-tool/RoomKindAdapter';
import { LinePrompt } from '../modules/line-detection/types';
import { MaskToolDebugKit } from '../widgets/MaskToolDebugKit';
import { PipelineControlPanel, PipelineInterface } from '../../../ui/pipeline/PipelineControlPanel';

/** Rooms already committed are punched out of the working map with this
 * pad (px), so a trace beside an existing room cannot pick its walls up. */
const ROOM_PUNCH_PAD_PX = 10;

const SETTINGS_CONFIG_PATH = '/config/segmentation/loop-tracer-settings.json';

/** An OPEN run (the hovered wall closes no loop) previews in the wall
 * tracer's warning orange — the color IS the feedback. */
const OPEN_RUN_COLOR = 0xffaa00;
/** The hair a self-meeting closure CUT OUT, shown when the result is not
 * committable — red, like the debug view's discarded rays. */
const CUT_HAIR_COLOR = 0xff4444;

/** Settings baked into the map at capture time — changing one recaptures. */
const CAPTURE_BAKED_SETTINGS = new Set(['sealOpenings', 'wallDepthValue', 'wallDepthTolerance', 'doorDepthMin', 'doorDepthMax', 'closeRadiusPx', 'skeletonize', 'spurMaxPx', 'holeFillPx', 'postProcess', 'clipMin', 'minBlobPx']);

/** On-disk settings config (see MaskToolDebugKit.loadConfig): every field
 * optional, missing keys ride on the compiled defaults. */
interface LoopTraceSettingsFile {
  prep?: Partial<MaskPrepOptions>;
  loop?: Partial<LoopTraceOptions>;
  /** Bright blobs smaller than this many px are erased from the working
   * map before tracing (skeleton px ≈ line length). */
  minBlobPx?: number;
  /** Rings enclosing less than this are obstacles the trace grows around. */
  minRoomAreaM2?: number;
  postProcess?: boolean;
}

/** Same construction as the probe/tracer adapters: rooms commit through
 * the magic-room flow, without line detection running on enter. */
class LoopTraceRoomAdapter extends RoomKindAdapter {
  /** From the adapter interface (not declared on RoomKindAdapter itself). */
  readonly enableLineDetection = false;
  /** …but keep capturing viewport maps so density snapping works in edits. */
  readonly captureMapsForSnapping = true;

  override shouldRunSegmentationOnEnter(): boolean {
    return false;
  }
}

export class LoopTraceRoomTool extends SubToolHosting(FloorplanToolBase) {
  public name = 'loop trace room';

  private coordinateTransformer: CoordinateTransformer;
  private imageCapture: ImageCapture;
  private polygonProcessor: PolygonProcessor;
  /** Renders the traced polygon exactly like the magic room tool's preview
   * (green fill + purple outline + vertex dots). */
  private polygonRenderer: PolygonRenderer3D;

  /** Hosted LineTool + room adapter: a committed loop hands the polygon to
   * the same edit machinery the magic room tool uses. */
  private readonly lineTool: LineTool;
  private readonly roomAdapter: LoopTraceRoomAdapter;
  /** True while the hosted LineTool owns input for a room-edit session. */
  private roomEditing = false;
  /** Set synchronously when a click hands off to the (async) edit session,
   * so trailing events cannot start a second one. */
  private editSessionPending = false;

  /** Capture-prep parameters — tunable live from the debug panel, baked at
   * capture time. */
  private prep: MaskPrepOptions = { ...DEFAULT_MASK_PREP_OPTIONS };
  /** Master toggle for the reduction steps after the clip threshold. */
  private postProcess = true;
  /** Loop-trace parameters (threshold, minimum ring length). */
  private loopOptions: LoopTraceOptions = { ...DEFAULT_LOOP_TRACE_OPTIONS };
  /** Bright blobs smaller than this many px are erased from the working
   * map (skeleton px ≈ line length, so this is roughly a length filter). */
  private minBlobPx = 1000;
  /** Rings enclosing less than this are obstacles, not rooms — the trace
   * grows outward around them (see LoopTraceOptions.minAreaPx2). */
  private minRoomAreaM2 = 1;
  /** Debug-panel toggle: log every trace decision (snap, per-round search
   * outcome, gate verdicts) for HOVER traces too. Clicks always log. */
  private logTrace = false;
  /** Latest trace's decision lines, mirrored into the debug panel. */
  private traceLogLines: string[] = [];
  private traceLogEl: HTMLElement | null = null;
  /** Scale of the active capture, for the m² → px² conversion. */
  private capturedPxPerMeter: number | null = null;

  /** Pristine capture (rooms not yet punched out). */
  private baseMask: TracerMask | null = null;
  /** The RAW density screenshot — the interior-coverage gate measures
   * candidate rings against what was ORIGINALLY scanned, not against the
   * processed map. */
  private rawCapture: TracerMask | null = null;
  /** Working capture: `baseMask` minus committed rooms, blob-filtered. */
  private mask: TracerMask | null = null;
  private capturing = false;
  /** Settle timer: recapture once the camera stops moving. */
  private recaptureTimer: number | null = null;
  /** True from the first camera move until the next capture completes. */
  private mapStale = false;

  /** The traced + cleaned preview currently on screen (mask px). */
  private shape: TracerPoint[] | null = null;
  /** Last cleanAndRender input, so pipeline edits can live-refresh the shape. */
  private lastCleanInput: TracerPoint[] | null = null;
  /** Orange open-run / uncommittable-ring polylines, plus the red cut
   * hair when a self-meeting closure discarded one. */
  private feedbackLines: Line2[] = [];
  private feedbackGeometries: LineGeometry[] = [];
  private feedbackMaterials: LineMaterial[] = [];
  /** Live controls for the polygon cleanup pipeline (debug panel). */
  private pipelinePanel: PipelineControlPanel | null = null;
  /** Monotonic token so stale async cleanups never overwrite newer previews. */
  private previewToken = 0;

  private debugContainer: HTMLDivElement | null = null;
  /** Shared debug-panel machinery, parameterized with THIS tool's config. */
  private readonly debugKit = new MaskToolDebugKit({ configPath: SETTINGS_CONFIG_PATH, exportFilename: 'loop-tracer-settings.json' });
  private settingsControlsRoot: HTMLElement | null = null;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.coordinateTransformer = new CoordinateTransformer(viewer);
    this.imageCapture = new ImageCapture(viewer, this.coordinateTransformer);
    this.polygonRenderer = new PolygonRenderer3D(viewer, this.coordinateTransformer);
    // Own pipeline config file — a traced ring is skeleton-clean, so its
    // passes can be tuned independently of the probe's (and the two debug
    // panels must not edit each other's pipeline).
    this.polygonProcessor = new PolygonProcessor({ configPath: '/config/segmentation/loop-tracer-poly-pipeline-config.json' });
    void this.loadSettingsConfig();

    this.roomAdapter = new LoopTraceRoomAdapter();
    this.lineTool = new LineTool(viewer, persona, undefined, this.roomAdapter);
    const ctx = this.roomAdapter.createSelectionContext(viewer, persona);
    this.initSubToolHosting(this.lineTool, this.roomAdapter, ctx);
  }

  protected onEnable(): void {
    super.onEnable();
    this.setRoomEditRouting(false);
    this.reset();
    this.createDebugWidget();
    this.viewer.addEventListener('camera_changed', this.handleCameraChange);
    this.viewer.addEventListener('controls_changed', this.handleCameraChange);
    this.viewer.scene.addEventListener('room_added', this.handleRoomsChanged);
    this.viewer.scene.addEventListener('room_removed', this.handleRoomsChanged);
    this.viewer.scene.addEventListener('room_updated', this.handleRoomsChanged);
    this.captureView();
  }

  protected onDisable(): void {
    this.setRoomEditRouting(false);
    this.viewer.removeEventListener('camera_changed', this.handleCameraChange);
    this.viewer.removeEventListener('controls_changed', this.handleCameraChange);
    this.viewer.scene.removeEventListener('room_added', this.handleRoomsChanged);
    this.viewer.scene.removeEventListener('room_removed', this.handleRoomsChanged);
    this.viewer.scene.removeEventListener('room_updated', this.handleRoomsChanged);
    this.setMapStale(false);
    if (this.recaptureTimer !== null) {
      window.clearTimeout(this.recaptureTimer);
      this.recaptureTimer = null;
    }
    this.reset();
    this.destroyDebugWidget();
    this.mask = null;
    this.baseMask = null;
    this.rawCapture = null;
    super.onDisable();
  }

  /** Rooms changed — the punched-out areas of the working mask are stale. */
  private handleRoomsChanged = (): void => {
    if (!this.enabled) return;
    this.applyRoomPunches();
  };

  /** Route subtool input only during a room-edit session. */
  private setRoomEditRouting(active: boolean): void {
    const kinds: SubToolInputKind[] = ['pointer', 'click', 'keyboard', 'wheel'];
    for (const kind of kinds) this.muteInput(kind, !active);
    this.roomEditing = active;
  }

  /** A forwarded event may have ended the edit session (Enter created the
   * room, Esc discarded) — detect that and return to tracing. */
  private syncRoomEditState(): void {
    if (!this.roomEditing) return;
    const mode = this.lineTool.getActiveMode();
    if (mode.getLinePrompts().length > 0) return;
    this.setRoomEditRouting(false);
    mode.setLinePrompts([]);
    // A created room must be re-punched out of the capture.
    this.applyRoomPunches();
  }

  // ─── Capture ───────────────────────────────────────────────────────────

  /**
   * Camera pan/zoom invalidates the capture. Debounced: recapture once the
   * movement settles instead of re-rendering every frame.
   */
  private handleCameraChange = (): void => {
    if (!this.enabled) return;
    if (this.viewportMatchesCapture()) return;
    if (!this.roomEditing) this.setMapStale(true);
    if (this.recaptureTimer !== null) window.clearTimeout(this.recaptureTimer);
    this.recaptureTimer = window.setTimeout(() => {
      this.recaptureTimer = null;
      if (this.viewportMatchesCapture()) {
        this.setMapStale(false);
        return;
      }
      this.captureView();
    }, 250);
  };

  /** True when the current viewport maps to the same world corners the
   * active capture was taken from. */
  private viewportMatchesCapture(): boolean {
    const width = this.viewer.renderArea.clientWidth;
    const height = this.viewer.renderArea.clientHeight;
    const cornerTL = this.coordinateTransformer.screenToWorld(new THREE.Vector2(0, 0));
    const cornerBR = this.coordinateTransformer.screenToWorld(new THREE.Vector2(width, height));
    const stored = this.coordinateTransformer.getMaskCorners();
    return !!cornerTL && !!cornerBR && stored.length === 4 && stored[0].distanceTo(cornerTL) < 0.001 && stored[2].distanceTo(cornerBR) < 0.001;
  }

  /** Stale = the map on screen no longer matches the camera: spinner
   * cursor, no preview, no tracing until the next capture lands. */
  private setMapStale(stale: boolean): void {
    if (this.mapStale === stale) return;
    this.mapStale = stale;
    this.viewer.renderer.domElement.style.cursor = stale ? 'wait' : '';
    if (stale) {
      this.previewToken++;
      this.clearPreview();
    }
  }

  /** Build the working map: viewport density shot → maskComposition
   * (depth filter, close + void border, hole fill, skeleton, spur prune)
   * → room punches + blob filter. */
  private captureView(): void {
    if (this.capturing) return;
    this.capturing = true;
    try {
      const width = this.viewer.renderArea.clientWidth;
      const height = this.viewer.renderArea.clientHeight;
      const shot = this.viewer.screenshotRenderer.renderScreenshot(new THREE.Vector2(0, 0), width, height, false, 1.0, true);
      const pixelWidth = Math.round(shot.pixelWidth);
      const pixelHeight = Math.round(shot.pixelHeight);
      if (shot.buffer.length !== pixelWidth * pixelHeight * 4) return;

      const screenCorners = [new THREE.Vector2(0, 0), new THREE.Vector2(width, 0), new THREE.Vector2(width, height), new THREE.Vector2(0, height)];
      const worldCorners: THREE.Vector3[] = [];
      for (const corner of screenCorners) {
        const world = this.coordinateTransformer.screenToWorld(corner);
        if (!world) return;
        worldCorners.push(world);
      }
      this.coordinateTransformer.setMaskCorners(worldCorners);
      this.coordinateTransformer.setCapturedImage({ canvas: null, width: pixelWidth, height: pixelHeight });

      const pxPerMeter = this.coordinateTransformer.calculatePixelsPerMeter();
      this.polygonProcessor.setPixelsPerMeter(pxPerMeter ?? 1);
      // The minimum-room-area gate lives in px² at THIS capture's scale.
      this.capturedPxPerMeter = pxPerMeter ?? null;
      this.updateMinAreaPx2();

      // Position map for the wall depth filter and the cleanup pipeline's
      // pixel alignment (needs the encoding metadata, or it reads garbage).
      const floorZForMap = this.currentFloorFloorPlane?.height ?? 0;
      const ceilingZForMap = this.currentFloorCeilingPlane?.height ?? floorZForMap + 3;
      const zRange: [number, number] = [Math.min(floorZForMap, ceilingZForMap) - 0.5, Math.max(floorZForMap, ceilingZForMap) + 0.5];
      const positionMap = this.imageCapture.capturePositionMap(new THREE.Vector2(0, 0), new THREE.Vector2(width, height), pixelWidth, pixelHeight, zRange);
      if (positionMap) {
        this.coordinateTransformer.setPositionMapEncoding({
          eyeMin: positionMap.eyeMin,
          eyeMax: positionMap.eyeMax,
          inverseViewMatrix: positionMap.inverseViewMatrix,
        });
        this.polygonProcessor.setPositionMap(positionMap.imageData);
      }

      const raw: TracerMask = { data: new Uint8ClampedArray(shot.buffer), width: pixelWidth, height: pixelHeight };
      this.rawCapture = raw;
      this.debugKit.resetStages();
      const stageSink: MaskStageSink | undefined = this.debugContainer
        ? (stage, data): void => {
          this.debugKit.pushStage({ name: stage, data, width: pixelWidth, height: pixelHeight });
        }
        : undefined;
      const composed = composeWorkingMask(raw, positionMap ? positionMap.imageData : null, this.maskPrep(), this.loopOptions.clipMin, stageSink);
      this.baseMask = composed.mask;
      this.applyRoomPunches();
    } finally {
      this.capturing = false;
      this.setMapStale(false);
    }
  }

  /** The capture-prep parameters currently in effect (the post-process
   * master toggle gates the reduction steps without losing their values). */
  private maskPrep(): MaskPrepOptions {
    if (this.postProcess) return { ...this.prep };
    return { ...this.prep, skeletonize: false, shellPx: 0 };
  }

  /** Re-stamp the working mask from the pristine capture with committed
   * rooms punched black, then blob-filter the leftovers. */
  private applyRoomPunches(): void {
    if (!this.baseMask) return;
    const data = new Uint8ClampedArray(this.baseMask.data);
    const { width, height } = this.baseMask;

    const worldPolygons = this.getCommittedRoomPolygons();
    if (worldPolygons.length > 0) {
      const normalized = worldPolygons.map((polygon) =>
        polygon.map((point) => {
          const uv = this.coordinateTransformer.worldToMaskSpace(point);
          return { x: uv.x, y: uv.y };
        }),
      );
      const inside = DepthSegmentationPipeline.rasterizePolygonMask(normalized, width, height);
      const padded = dilateMask(inside, width, height, ROOM_PUNCH_PAD_PX);
      for (let i = 0; i < padded.length; i++) {
        if (padded[i] === 0) continue;
        data[i * 4] = 0;
        data[i * 4 + 1] = 0;
        data[i * 4 + 2] = 0;
      }
    }

    const punched: TracerMask = { data, width, height };
    this.mask = removeSmallBlobs(punched, this.loopOptions.clipMin, this.postProcess ? this.minBlobPx : 0);
    this.polygonProcessor.setDensityMask(new ImageData(new Uint8ClampedArray(this.mask.data), width, height));
    if (this.debugContainer) this.debugKit.setFinalStage('punches + blob filter (final)', this.mask);
  }

  /** World-space polygons of the rooms already committed on this floor. */
  private getCommittedRoomPolygons(): THREE.Vector3[][] {
    const polygons: THREE.Vector3[][] = [];
    for (const room of this.viewer.scene.rooms as PointoramaRoom[]) {
      if (!room.visible) continue;
      if (room.groupIdentifier !== this.currentFloorGroupIdentifier) continue;
      const polygon = room.getPolygon();
      if (polygon.length < 3) continue;
      polygons.push(polygon);
    }
    return polygons;
  }

  // ─── Coordinate helpers ────────────────────────────────────────────────

  private eventToMaskPx(event: MouseEvent): TracerPoint | null {
    const rect = this.viewer.renderArea.getBoundingClientRect();
    const screen = new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
    if (!this.mask) return null;
    const world = this.coordinateTransformer.screenToWorld(screen);
    if (!world) return null;
    let uv: THREE.Vector2;
    try {
      uv = this.coordinateTransformer.worldToMaskSpace(world);
    } catch {
      return null;
    }
    const x = uv.x * this.mask.width;
    const y = uv.y * this.mask.height;
    if (x < 0 || x >= this.mask.width || y < 0 || y >= this.mask.height) return null;
    return { x, y };
  }

  private maskPxToWorld(p: TracerPoint): THREE.Vector3 {
    const mask = this.mask;
    const u = mask ? p.x / mask.width : 0;
    const v = mask ? p.y / mask.height : 0;
    const world = this.coordinateTransformer.maskSpaceToWorld(new THREE.Vector2(u, v));
    world.z += 0.05;
    return world;
  }

  // ─── Input ─────────────────────────────────────────────────────────────

  public onMouseMove(event: MouseEvent): void {
    if (this.roomEditing) {
      super.onMouseMove(event);
      this.syncRoomEditState();
      return;
    }
    if (this.editSessionPending || this.mapStale || !this.mask) return;
    // Hover previews the loop a click here would commit — same trace,
    // same cleanup.
    const seed = this.eventToMaskPx(event);
    const traced = seed ? traceShortestLoop(this.mask, seed, this.loopOptions, this.rawCapture ?? undefined, this.traceLogger()) : null;
    if (!traced) {
      this.clearPreview();
      return;
    }
    if (!traced.closed) {
      // Nothing committable here — show the open run / undersized ring in
      // warning orange, and the hair a self-meeting closure cut out in
      // red, instead of silently showing nothing.
      this.renderFeedback(traced.loop, traced.hair ?? null);
      return;
    }
    void this.cleanAndRender(traced.loop);
  }

  public onClick(event: MouseEvent): void {
    if (this.roomEditing) {
      super.onClick(event);
      this.syncRoomEditState();
      return;
    }
    if (this.editSessionPending || this.mapStale || !this.mask) return;
    const seed = this.eventToMaskPx(event);
    const traced = seed ? traceShortestLoop(this.mask, seed, this.loopOptions, this.rawCapture ?? undefined, this.traceLogger(true)) : null;
    // Only a CLOSED ring can become a room — a failed click renders the
    // same feedback the hover shows (and has just logged WHY).
    if (!traced) return;
    if (!traced.closed) {
      this.renderFeedback(traced.loop, traced.hair ?? null);
      return;
    }
    // Commit exactly what the hover shows: clean the same loop and hand it
    // to the room-edit session.
    this.editSessionPending = true;
    void this.enterEditSession(traced.loop).finally(() => {
      this.editSessionPending = false;
    });
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (this.roomEditing) {
      super.onKeyDown(event);
      this.syncRoomEditState();
      return;
    }
    if (event.key === 'Escape') this.clearPreview();
  }


  /** The tracer's per-decision logger. Clicks always log (`force`);
   * hover traces only while the debug toggle is on. Lines go to the
   * console AND the debug panel's readout. Intentional debug output. */
  private traceLogger(force = false): ((message: string) => void) | undefined {
    if (!this.logTrace && !force) return undefined;
    this.traceLogLines = [];
    return (message: string): void => {
      console.debug('[LoopTrace]', message);
      this.traceLogLines.push(message);
      if (this.traceLogEl) this.traceLogEl.textContent = this.traceLogLines.join('\n');
    };
  }

  // ─── Cleanup + session ─────────────────────────────────────────────────

  /** Run the traced ring through the polygon pipeline (in cm space, so
   * the passes' cm tolerances apply) and render the preview. */
  private async cleanAndRender(ringPx: TracerPoint[]): Promise<void> {
    const token = ++this.previewToken;
    this.lastCleanInput = ringPx;
    const cleaned = await this.cleanRing(ringPx);
    if (token !== this.previewToken) return; // superseded by a newer hover
    this.shape = cleaned;
    if (cleaned.length < 3) {
      this.clearPreview();
      return;
    }
    this.clearOpenRun();
    this.polygonRenderer.renderPolygons([createPolygon(cleaned.map((p) => ({ x: p.x, y: p.y })))], null);
  }

  /** Feedback polylines for a non-committable result: the run/ring in
   * warning orange, the cut hair (when present) in red. */
  private renderFeedback(pathPx: TracerPoint[], hairPx: TracerPoint[] | null): void {
    this.previewToken++; // drop any in-flight closed-loop cleanup
    this.shape = null;
    this.polygonRenderer.clearPolygonRendering();
    this.clearOpenRun();
    this.addFeedbackLine(pathPx, OPEN_RUN_COLOR);
    if (hairPx) this.addFeedbackLine(hairPx, CUT_HAIR_COLOR);
  }

  private addFeedbackLine(pathPx: TracerPoint[], color: number): void {
    if (pathPx.length < 2) return;
    const positions: number[] = [];
    for (const p of pathPx) {
      const world = this.maskPxToWorld(p);
      positions.push(world.x, world.y, world.z);
    }
    const geometry = new LineGeometry();
    geometry.setPositions(positions);
    const material = new LineMaterial({
      color,
      linewidth: 3,
      resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      depthTest: false,
    });
    const line = new Line2(geometry, material);
    line.renderOrder = 999;
    this.viewer.uiScene.add(line);
    this.feedbackLines.push(line);
    this.feedbackGeometries.push(geometry);
    this.feedbackMaterials.push(material);
  }

  private clearOpenRun(): void {
    for (const line of this.feedbackLines) this.viewer.uiScene.remove(line);
    for (const geometry of this.feedbackGeometries) geometry.dispose();
    for (const material of this.feedbackMaterials) material.dispose();
    this.feedbackLines = [];
    this.feedbackGeometries = [];
    this.feedbackMaterials = [];
  }

  private async cleanRing(ringPx: TracerPoint[]): Promise<TracerPoint[]> {
    const pixelsPerMeter = this.coordinateTransformer.calculatePixelsPerMeter();
    if (!pixelsPerMeter || pixelsPerMeter <= 0) return ringPx;
    const pxPerCm = pixelsPerMeter / 100;
    const cmPoints: Point2D[] = ringPx.map((p) => ({ x: p.x / pxPerCm, y: p.y / pxPerCm }));
    try {
      await this.polygonProcessor.waitForPipelineLoad();
      // Each trace must be independent — a cached point from the previous
      // hover position would contaminate this one (preview == commit).
      this.polygonProcessor.getPipeline().clearPointCaches();
      const processed = await this.polygonProcessor.getPipeline().process([createPolygon(cmPoints)], { simplify: true });
      if (!processed[0] || processed[0].points.length < 3) return ringPx;
      const mapped = processed[0].points.map((p) => ({ x: p.x * pxPerCm, y: p.y * pxPerCm }));
      const finite = mapped.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
      return finite.length >= 3 ? finite : ringPx;
    } catch {
      return ringPx; // pipeline unavailable — keep the raw ring
    }
  }

  /** Hand the cleaned loop to the hosted LineTool as edge prompts and
   * start the magic-room NEW-shape flow (drag edges/vertices, Enter
   * creates the room, Esc discards) — the wall tracer's handoff. */
  private async enterEditSession(ringPx: TracerPoint[]): Promise<void> {
    const cleaned = await this.cleanRing(ringPx);
    const points = cleaned.slice();
    if (points.length > 2 && Math.hypot(points[0].x - points[points.length - 1].x, points[0].y - points[points.length - 1].y) < 2) {
      points.pop();
    }
    if (points.length < 3 || this.roomEditing) return;

    const floorZ = this.currentFloorFloorPlane?.height ?? 0;
    const world = points.map((p) => {
      const w = this.maskPxToWorld(p);
      w.z = floorZ;
      return w;
    });
    const prompts: LinePrompt[] = world.map((a, i) => {
      const b = world[(i + 1) % world.length];
      const midpoint = a.clone().add(b).multiplyScalar(0.5);
      return {
        id: `prompt_${i}`,
        worldPoint: midpoint,
        direction: b.clone().sub(a).normalize(),
        screenPosition: this.lineTool.coordinateTransformer.worldToScreen(midpoint),
      };
    });

    this.reset();
    const mode = this.lineTool.getActiveMode();
    mode.setLinePrompts(prompts);
    mode.updateShapePreview();
    mode.refreshDetection();
    this.setRoomEditRouting(true);
  }

  private reset(): void {
    this.previewToken++;
    this.shape = null;
    this.clearPreview();
  }

  private clearPreview(): void {
    this.shape = null;
    this.polygonRenderer.clearPolygonRendering();
    this.clearOpenRun();
  }

  // ─── Settings (debug panel) ────────────────────────────────────────────

  private async loadSettingsConfig(): Promise<void> {
    const file = await this.debugKit.loadConfig<LoopTraceSettingsFile>();
    if (!file) return;
    this.prep = { ...this.prep, ...(file.prep ?? {}) };
    this.loopOptions = { ...this.loopOptions, ...(file.loop ?? {}) };
    // minAreaPx2 is capture-scale-dependent — never taken from disk.
    this.updateMinAreaPx2();
    if (typeof file.minBlobPx === 'number') this.minBlobPx = file.minBlobPx;
    if (typeof file.minRoomAreaM2 === 'number') {
      this.minRoomAreaM2 = file.minRoomAreaM2;
      this.updateMinAreaPx2();
    }
    if (typeof file.postProcess === 'boolean') this.postProcess = file.postProcess;
    this.refreshSettingsControls();
    if (this.enabled && this.mask) this.captureView();
  }

  private exportSettings(): void {
    const file: LoopTraceSettingsFile = {
      prep: { ...this.prep },
      // minAreaPx2 is capture-scale-dependent — the m² value is the config.
      loop: { clipMin: this.loopOptions.clipMin, minLoopPx: this.loopOptions.minLoopPx, minInteriorCoverage: this.loopOptions.minInteriorCoverage },
      minBlobPx: this.minBlobPx,
      minRoomAreaM2: this.minRoomAreaM2,
      postProcess: this.postProcess,
    };
    this.debugKit.exportConfig(file);
  }

  /** Single entry point for every tunable — capture-baked keys recapture,
   * everything else applies on the next hover. */
  private applySetting(key: string, value: number | boolean): void {
    if (key === 'sealOpenings') this.prep.sealOpenings = Boolean(value);
    if (key === 'wallDepthValue') this.prep.wallDepthValue = Number(value);
    if (key === 'wallDepthTolerance') this.prep.wallDepthTolerance = Number(value);
    if (key === 'doorDepthMin') this.prep.doorDepthMin = Number(value);
    if (key === 'doorDepthMax') this.prep.doorDepthMax = Number(value);
    if (key === 'closeRadiusPx') this.prep.closeRadiusPx = Number(value);
    if (key === 'skeletonize') this.prep.skeletonize = Boolean(value);
    if (key === 'spurMaxPx') this.prep.spurMaxPx = Number(value);
    if (key === 'holeFillPx') this.prep.holeFillPx = Number(value);
    if (key === 'postProcess') this.postProcess = Boolean(value);
    if (key === 'clipMin') this.loopOptions.clipMin = Number(value);
    if (key === 'minBlobPx') this.minBlobPx = Number(value);
    if (CAPTURE_BAKED_SETTINGS.has(key)) {
      this.captureView();
      return;
    }
    if (key === 'minLoopPx') this.loopOptions.minLoopPx = Number(value);
    if (key === 'minInteriorCoverage') this.loopOptions.minInteriorCoverage = Number(value);
    if (key === 'logTrace') this.logTrace = Boolean(value);
    if (key === 'minRoomAreaM2') {
      this.minRoomAreaM2 = Number(value);
      this.updateMinAreaPx2();
    }
  }

  /** m² → px² at the active capture's scale (0 while no scale is known —
   * growing then stays off rather than guessing). */
  private updateMinAreaPx2(): void {
    const pxPerMeter = this.capturedPxPerMeter;
    this.loopOptions.minAreaPx2 = pxPerMeter && pxPerMeter > 0 ? this.minRoomAreaM2 * pxPerMeter * pxPerMeter : 0;
  }

  private refreshSettingsControls(): void {
    if (!this.settingsControlsRoot) return;
    const fresh = this.buildSettingsControls();
    this.settingsControlsRoot.replaceWith(fresh);
    this.settingsControlsRoot = fresh;
  }

  private buildSettingsControls(): HTMLElement {
    const root = document.createElement('div');

    let body = this.debugKit.section(root, 'Capture filters');
    this.debugKit.slider(body, 'Wall depth', 0, 255, 1, this.prep.wallDepthValue, (v) => this.applySetting('wallDepthValue', v));
    this.debugKit.slider(body, 'Wall depth tolerance (128 = off)', 0, 128, 1, this.prep.wallDepthTolerance, (v) => this.applySetting('wallDepthTolerance', v));
    this.debugKit.band(body, 'Door depth band', 0, 255, this.prep.doorDepthMin, this.prep.doorDepthMax, (lo, hi) => {
      this.prep.doorDepthMin = lo;
      this.prep.doorDepthMax = hi;
      this.captureView();
    });
    this.debugKit.toggle(body, 'Opening zones', this.prep.sealOpenings, (v) => this.applySetting('sealOpenings', v));
    this.debugKit.slider(body, 'Close radius', 0, 40, 1, this.prep.closeRadiusPx, (v) => this.applySetting('closeRadiusPx', v));

    body = this.debugKit.section(root, 'Clipping');
    this.debugKit.slider(body, 'Wall threshold', 0, 255, 5, this.loopOptions.clipMin, (v) => this.applySetting('clipMin', v));

    const ppBody = this.debugKit.toggleSection(root, 'Post-processing', this.postProcess, (v) => this.applySetting('postProcess', v));
    this.debugKit.toggle(ppBody, 'Skeletonize (off = shell hollow)', this.prep.skeletonize, (v) => this.applySetting('skeletonize', v));
    this.debugKit.slider(ppBody, 'Spur prune length', 0, 60, 1, this.prep.spurMaxPx, (v) => this.applySetting('spurMaxPx', v));
    this.debugKit.slider(ppBody, 'Fill holes up to (px)', 0, 10000, 100, this.prep.holeFillPx, (v) => this.applySetting('holeFillPx', v));
    this.debugKit.slider(ppBody, 'Min object size', 0, 20000, 100, this.minBlobPx, (v) => this.applySetting('minBlobPx', v));

    body = this.debugKit.section(root, 'Loop trace');
    this.debugKit.slider(body, 'Min loop length', 8, 400, 4, this.loopOptions.minLoopPx, (v) => this.applySetting('minLoopPx', v));
    this.debugKit.slider(body, 'Min room area (m²)', 0, 10, 0.25, this.minRoomAreaM2, (v) => this.applySetting('minRoomAreaM2', v));
    this.debugKit.slider(body, 'Min interior coverage', 0, 1, 0.05, this.loopOptions.minInteriorCoverage, (v) => this.applySetting('minInteriorCoverage', v));
    this.debugKit.toggle(body, 'Log trace decisions (hover too)', this.logTrace, (v) => this.applySetting('logTrace', v));
    // Readout of the LATEST trace's decisions — clicks always fill it.
    this.traceLogEl = document.createElement('pre');
    this.traceLogEl.style.cssText = `margin: 4px 0; padding: 6px; max-height: 180px; overflow-y: auto; font-size: 10px; line-height: 1.4; color: rgba(255,255,255,0.8); background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; white-space: pre-wrap;`;
    this.traceLogEl.textContent = this.traceLogLines.length > 0 ? this.traceLogLines.join('\n') : '(click to trace — decisions appear here)';
    body.appendChild(this.traceLogEl);

    return root;
  }

  // ─── Debug widget ──────────────────────────────────────────────────────

  private createDebugWidget(): void {
    if (this.debugContainer || !this.viewer.debugOverlay) return;
    const container = document.createElement('div');
    container.style.cssText = `margin-top: 8px;`;

    const title = document.createElement('div');
    title.style.cssText = `font-size: 12px; font-weight: bold; color: rgba(255,255,255,0.9); margin-bottom: 4px;`;
    title.textContent = 'Loop tracer (skeleton map + stages):';
    container.appendChild(title);

    this.debugKit.attachStageStrip(container);
    this.settingsControlsRoot = this.buildSettingsControls();
    container.appendChild(this.settingsControlsRoot);
    this.debugKit.exportButton(container, () => this.exportSettings());

    // Live controls for the polygon cleanup pipeline — any pass edit
    // (toggle/params/add/reorder) re-cleans the current preview
    // immediately, same behaviour as the probe's and wall tracer's panels.
    this.pipelinePanel = new PipelineControlPanel({
      onPipelineChanged: () => {
        if (this.lastCleanInput) void this.cleanAndRender(this.lastCleanInput);
      },
    });
    this.pipelinePanel.addTab({
      id: 'loopTracePolygon',
      label: 'Loop Cleanup',
      pipeline: this.polygonProcessor.getPipeline() as unknown as PipelineInterface,
    });
    container.appendChild(this.pipelinePanel.getElement());

    this.debugContainer = container;
    this.viewer.debugOverlay.addWidget({
      name: 'loop-trace',
      container,
      update: () => this.pipelinePanel?.refreshTimings(),
      destroy: () => {
        if (container.parentElement) container.parentElement.removeChild(container);
      },
    });
  }

  private destroyDebugWidget(): void {
    if (!this.debugContainer) return;
    this.viewer.debugOverlay?.removeWidget('loop-trace');
    this.debugContainer = null;
    this.debugKit.detach();
    this.settingsControlsRoot = null;
    this.traceLogEl = null;
    this.pipelinePanel = null;
  }
}
